import { buf as crc32buf } from 'crc-32';
import { Any } from '~/common/utils';

/**
 * ZIP Parser with streaming support
 * Parses ZIP central directory and streams entries without loading full files into memory
 */

export interface ZipEntryMetadata {
  name: string;
  isDirectory: boolean;
  compressedSize: number;
  uncompressedSize: number;
  compressionMethod: number; // 0 = stored, 8 = deflate
  crc32: number;
  lastModified: Date;

  // Internal offsets for streaming
  _headerOffset: number;
  _nameLength: number;
  _extraFieldLength: number;
}

export interface StreamValidationResult {
  valid: boolean;
  reason?: string;
  data?: ArrayBuffer; // Only if you need the full buffer for further validation
}

export interface ZipParseResult {
  entries: ZipEntryMetadata[];
  namedEntries: Record<string, ZipEntryMetadata>;
}

/**
 * Main ZIP parser class
 */
export class ZipParser {
  constructor(private file: Blob) {}

  /**
   * Parse ZIP central directory and return all entry metadata
   * Does NOT load file contents into memory
   */
  async parse(): Promise<ZipParseResult> {
    const minEocdSize = 22;
    const searchSize = Math.min(65557, this.file.size); // Max comment size + EOCD
    const endBuf = await this.file.slice(-searchSize).arrayBuffer();
    const endView = new DataView(endBuf);

    // Find EOCD signature (0x06054b50) searching backwards
    let eocdOffset = -1;
    for (let i = endBuf.byteLength - minEocdSize; i >= 0; i--) {
      if (endView.getUint32(i, true) === 0x06054b50) {
        eocdOffset = i;
        break;
      }
    }

    if (eocdOffset === -1) {
      throw new Error('Invalid ZIP: End of central directory not found');
    }

    const cdOffset = endView.getUint32(eocdOffset + 16, true);
    const cdSize = endView.getUint32(eocdOffset + 12, true);

    // Read central directory
    const cdBuf = await this.file.slice(cdOffset, cdOffset + cdSize).arrayBuffer();
    const cdView = new DataView(cdBuf);

    const entries: ZipEntryMetadata[] = [];
    let pos = 0;

    while (pos + 46 <= cdSize) {
      const sig = cdView.getUint32(pos, true);
      if (sig !== 0x02014b50) break; // Central file header signature

      const compressionMethod = cdView.getUint16(pos + 10, true);
      const lastModTime = cdView.getUint16(pos + 12, true);
      const lastModDate = cdView.getUint16(pos + 14, true);
      const crc32 = cdView.getUint32(pos + 16, true);
      const compressedSize = cdView.getUint32(pos + 20, true);
      const uncompressedSize = cdView.getUint32(pos + 24, true);
      const nameLength = cdView.getUint16(pos + 28, true);
      const extraFieldLength = cdView.getUint16(pos + 30, true);
      const commentLength = cdView.getUint16(pos + 32, true);
      const externalFileAttributes = cdView.getUint32(pos + 38, true);
      const headerOffset = cdView.getUint32(pos + 42, true);

      // Read filename
      const nameBytes = new Uint8Array(cdBuf, pos + 46, nameLength);
      const name = new TextDecoder().decode(nameBytes);

      // Check if directory (ends with / or has directory flag)
      const isDirectory = name.endsWith('/') || (externalFileAttributes & 0x10) !== 0;

      // Convert DOS date/time to JS Date
      const year = ((lastModDate >> 9) & 0x7f) + 1980;
      const month = (lastModDate >> 5) & 0x0f;
      const day = lastModDate & 0x1f;
      const hour = (lastModTime >> 11) & 0x1f;
      const minute = (lastModTime >> 5) & 0x3f;
      const second = (lastModTime & 0x1f) * 2;
      const lastModified = new Date(year, month - 1, day, hour, minute, second);

      // Read local header to get actual field lengths (can differ from central directory)
      const localHeaderBuf = await this.file.slice(headerOffset, headerOffset + 30).arrayBuffer();
      const localView = new DataView(localHeaderBuf);
      const localNameLength = localView.getUint16(26, true);
      const localExtraFieldLength = localView.getUint16(28, true);

      entries.push({
        name,
        isDirectory,
        compressedSize,
        uncompressedSize,
        compressionMethod,
        crc32,
        lastModified,
        _headerOffset: headerOffset,
        _nameLength: localNameLength,
        _extraFieldLength: localExtraFieldLength,
      });

      pos += 46 + nameLength + extraFieldLength + commentLength;
    }

    const namedEntries = Object.fromEntries(entries.map((e) => [e.name, e]));

    return { entries, namedEntries };
  }

  /**
   * Stream an entry and validate CRC-32 incrementally
   * Returns validated data if needed for further processing
   *
   * @param entry - Entry metadata from parse()
   * @param validateOnly - If true, skips building final ArrayBuffer (saves memory)
   */
  async streamAndValidate(entry: ZipEntryMetadata): Promise<StreamValidationResult> {
    // Calculate data offset: local header (30 bytes) + name + extra field
    const dataOffset = entry._headerOffset + 30 + entry._nameLength + entry._extraFieldLength;
    const dataEnd = dataOffset + entry.compressedSize;

    // Slice to get compressed data
    const compressedBlob = this.file.slice(dataOffset, dataEnd);

    let stream: ReadableStream<Uint8Array>;

    if (entry.compressionMethod === 8) {
      // DEFLATE compression
      stream = (compressedBlob.stream() as unknown as ReadableStream<Any>).pipeThrough(
        new DecompressionStream('deflate-raw'),
      ) as ReadableStream<Uint8Array>;
    } else if (entry.compressionMethod === 0) {
      stream = compressedBlob.stream() as unknown as ReadableStream<Any>;
    } else {
      return {
        valid: false,
        reason: `Unsupported compression method: ${entry.compressionMethod}`,
      };
    }

    // Stream and compute CRC-32 incrementally
    const reader = stream.getReader();
    let crc = 0;
    let totalBytes = 0;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Update CRC incrementally
        crc = crc32buf(value, crc);
        totalBytes += value.length;
      }
    } catch (e) {
      return {
        valid: false,
        reason: `Decompression failed: ${(e as Error).message}`,
      };
    } finally {
      reader.releaseLock();
    }

    // Validate CRC-32
    const computed = crc >>> 0;
    const stored = entry.crc32 >>> 0;

    if (computed !== stored) {
      return {
        valid: false,
        reason: `CRC-32 mismatch: stored ${stored}, computed ${computed}`,
      };
    }

    // Validate uncompressed size
    if (totalBytes !== entry.uncompressedSize) {
      return {
        valid: false,
        reason: `Size mismatch: expected ${entry.uncompressedSize}, got ${totalBytes}`,
      };
    }

    return { valid: true };
  }

  /**
   * Validate CRC-32 and extract magic bytes in a single pass (optimized)
   * Eliminates duplicate decompression for validation + file type detection
   *
   * @param entry - Entry metadata from parse()
   * @param magicByteCount - Number of magic bytes to capture (default: 16)
   */
  async validateAndGetMagicBytes(
    entry: ZipEntryMetadata,
    magicByteCount: number = 16,
  ): Promise<{ valid: boolean; reason?: string; magicBytes?: Uint8Array }> {
    const dataOffset = entry._headerOffset + 30 + entry._nameLength + entry._extraFieldLength;
    const dataEnd = dataOffset + entry.compressedSize;
    const compressedBlob = this.file.slice(dataOffset, dataEnd);

    let stream: ReadableStream<Uint8Array>;

    if (entry.compressionMethod === 8) {
      stream = (compressedBlob.stream() as unknown as ReadableStream<Any>).pipeThrough(
        new DecompressionStream('deflate-raw'),
      ) as ReadableStream<Uint8Array>;
    } else if (entry.compressionMethod === 0) {
      stream = compressedBlob.stream() as unknown as ReadableStream<Any>;
    } else {
      return {
        valid: false,
        reason: `Unsupported compression method: ${entry.compressionMethod}`,
      };
    }

    const reader = stream.getReader();
    let crc = 0;
    let totalBytes = 0;
    const magicBytes = new Uint8Array(magicByteCount);
    let magicBytesCollected = 0;
    let chunkCount = 0;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Yielding removed - unnecessary overhead in worker context

        // Collect magic bytes from first chunk(s)
        if (magicBytesCollected < magicByteCount) {
          const bytesToCopy = Math.min(value.length, magicByteCount - magicBytesCollected);
          magicBytes.set(value.subarray(0, bytesToCopy), magicBytesCollected);
          magicBytesCollected += bytesToCopy;
        }

        // Update CRC incrementally
        crc = crc32buf(value, crc);
        totalBytes += value.length;
        // eslint-disable-next-line unused-imports/no-unused-vars
        chunkCount++;
      }
    } catch (e) {
      return {
        valid: false,
        reason: `Decompression failed: ${(e as Error).message}`,
      };
    } finally {
      reader.releaseLock();
    }

    // Validate CRC-32
    const computed = crc >>> 0;
    const stored = entry.crc32 >>> 0;

    if (computed !== stored) {
      return {
        valid: false,
        reason: `CRC-32 mismatch: stored ${stored}, computed ${computed}`,
      };
    }

    // Validate uncompressed size
    if (totalBytes !== entry.uncompressedSize) {
      return {
        valid: false,
        reason: `Size mismatch: expected ${entry.uncompressedSize}, got ${totalBytes}`,
      };
    }

    return {
      valid: true,
      magicBytes: magicBytes.subarray(0, magicBytesCollected),
    };
  }

  /**
   * Get ReadableStream for an entry
   * Most efficient - returns stream directly without CRC validation
   * Use after CRC has been validated separately
   *
   * @param entry - Entry metadata from parse()
   */
  async getStream(entry: ZipEntryMetadata): Promise<ReadableStream<Uint8Array>> {
    const dataOffset = entry._headerOffset + 30 + entry._nameLength + entry._extraFieldLength;
    const dataEnd = dataOffset + entry.compressedSize;
    const compressedBlob = this.file.slice(dataOffset, dataEnd);

    let stream: ReadableStream<Uint8Array>;

    if (entry.compressionMethod === 8) {
      stream = (compressedBlob.stream() as unknown as ReadableStream<Any>).pipeThrough(
        new DecompressionStream('deflate-raw'),
      ) as ReadableStream<Uint8Array>;
    } else if (entry.compressionMethod === 0) {
      stream = compressedBlob.stream() as unknown as ReadableStream<Any>;
    } else {
      throw new Error(`Unsupported compression method: ${entry.compressionMethod}`);
    }

    return stream;
  }

  /**
   * Stream an entry and process chunks with a callback
   * Most memory-efficient option - never builds full ArrayBuffer
   *
   * @param entry - Entry metadata from parse()
   * @param onChunk - Called for each decompressed chunk
   */
  async streamWithCallback(
    entry: ZipEntryMetadata,
    onChunk: (chunk: Uint8Array) => void | Promise<void>,
  ): Promise<{ valid: boolean; reason?: string }> {
    const dataOffset = entry._headerOffset + 30 + entry._nameLength + entry._extraFieldLength;
    const dataEnd = dataOffset + entry.compressedSize;
    const compressedBlob = this.file.slice(dataOffset, dataEnd);

    let stream: ReadableStream<Uint8Array>;

    if (entry.compressionMethod === 8) {
      stream = (compressedBlob.stream() as unknown as ReadableStream<Any>).pipeThrough(
        new DecompressionStream('deflate-raw'),
      );
    } else if (entry.compressionMethod === 0) {
      stream = compressedBlob.stream() as unknown as ReadableStream<Any>;
    } else {
      return {
        valid: false,
        reason: `Unsupported compression method: ${entry.compressionMethod}`,
      };
    }

    const reader = stream.getReader();
    let crc = 0;
    let totalBytes = 0;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        crc = crc32buf(value, crc);
        totalBytes += value.length;

        // Call user callback
        await onChunk(value);
      }
    } catch (e) {
      return {
        valid: false,
        reason: `Processing failed: ${(e as Error).message}`,
      };
    } finally {
      reader.releaseLock();
    }

    // Validate
    const computed = crc >>> 0;
    const stored = entry.crc32 >>> 0;

    if (computed !== stored) {
      return {
        valid: false,
        reason: `CRC-32 mismatch: stored ${stored}, computed ${computed}`,
      };
    }

    if (totalBytes !== entry.uncompressedSize) {
      return {
        valid: false,
        reason: `Size mismatch: expected ${entry.uncompressedSize}, got ${totalBytes}`,
      };
    }

    return { valid: true };
  }

  /**
   * Get first N bytes for magic byte / file type detection
   * Most efficient for file type checks - reads only what's needed
   *
   * @param entry - Entry metadata from parse()
   * @param byteCount - Number of bytes to read (default: 16)
   */
  async getMagicBytes(entry: ZipEntryMetadata, byteCount: number = 16): Promise<Uint8Array> {
    const dataOffset = entry._headerOffset + 30 + entry._nameLength + entry._extraFieldLength;
    const dataEnd = dataOffset + entry.compressedSize;
    const compressedBlob = this.file.slice(dataOffset, dataEnd);

    let stream: ReadableStream<Uint8Array>;

    if (entry.compressionMethod === 8) {
      stream = (compressedBlob.stream() as Any).pipeThrough(new DecompressionStream('deflate-raw'));
    } else if (entry.compressionMethod === 0) {
      stream = compressedBlob.stream() as Any;
    } else {
      throw new Error(`Unsupported compression method: ${entry.compressionMethod}`);
    }

    const reader = stream.getReader();
    const result = new Uint8Array(byteCount);
    let offset = 0;

    try {
      // eslint-disable-next-line no-constant-condition
      while (offset < byteCount) {
        const { done, value } = await reader.read();
        if (done) break;

        const bytesToCopy = Math.min(value.length, byteCount - offset);
        result.set(value.subarray(0, bytesToCopy), offset);
        offset += bytesToCopy;

        if (offset >= byteCount) break;
      }
    } finally {
      reader.releaseLock();
    }

    return result.subarray(0, offset);
  }

  /**
   * Read specific byte range from entry (start inclusive, end exclusive)
   * Streams through data until reaching the desired range
   *
   * @param entry - Entry metadata from parse()
   * @param start - Start offset (0-based)
   * @param end - End offset (exclusive)
   */
  async readBytes(entry: ZipEntryMetadata, start: number, end: number): Promise<Uint8Array> {
    if (start < 0 || end > entry.uncompressedSize || start >= end) {
      throw new Error(`Invalid byte range: ${start}-${end} for size ${entry.uncompressedSize}`);
    }

    const dataOffset = entry._headerOffset + 30 + entry._nameLength + entry._extraFieldLength;
    const dataEnd = dataOffset + entry.compressedSize;
    const compressedBlob = this.file.slice(dataOffset, dataEnd);

    let stream: ReadableStream<Uint8Array>;

    if (entry.compressionMethod === 8) {
      stream = (compressedBlob.stream() as Any).pipeThrough(new DecompressionStream('deflate-raw'));
    } else if (entry.compressionMethod === 0) {
      stream = compressedBlob.stream() as Any;
    } else {
      throw new Error(`Unsupported compression method: ${entry.compressionMethod}`);
    }

    const reader = stream.getReader();
    const length = end - start;
    const result = new Uint8Array(length);
    let currentPos = 0;
    let resultOffset = 0;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunkStart = currentPos;
        const chunkEnd = currentPos + value.length;

        // Skip chunks before our range
        if (chunkEnd <= start) {
          currentPos = chunkEnd;
          continue;
        }

        // We've read past our range, done
        if (chunkStart >= end) {
          break;
        }

        // This chunk overlaps our range
        const copyStart = Math.max(0, start - chunkStart);
        const copyEnd = Math.min(value.length, end - chunkStart);
        const bytesToCopy = copyEnd - copyStart;

        result.set(value.subarray(copyStart, copyEnd), resultOffset);
        resultOffset += bytesToCopy;
        currentPos = chunkEnd;

        if (currentPos >= end) break;
      }
    } finally {
      reader.releaseLock();
    }

    return result.subarray(0, resultOffset);
  }

  /**
   * Read last N bytes from entry (for tail validation like PDF %%EOF)
   *
   * @param entry - Entry metadata from parse()
   * @param byteCount - Number of bytes to read from end
   */
  async readTail(entry: ZipEntryMetadata, byteCount: number): Promise<Uint8Array> {
    const start = Math.max(0, entry.uncompressedSize - byteCount);
    return this.readBytes(entry, start, entry.uncompressedSize);
  }

  /**
   * Get decompressed data as ArrayBuffer
   * TODO: Re-implement with streaming validators
   * Currently disabled to avoid memory issues with large files
   */
  // async getArrayBuffer(
  //   entry: ZipEntryMetadata,
  //   _validateCrc: boolean = true,
  // ): Promise<ArrayBuffer> {
  //   throw new Error('Not implemented - use streaming validators instead');
  // }
}

/**
 * Usage example:
 *
 * const parser = new ZipParser(file);
 * const { entries, namedEntries } = await parser.parse();
 *
 * for (const entry of entries) {
 *   if (entry.isDirectory) continue;
 *
 *   // Option 1: Check magic bytes (most efficient for file type detection)
 *   const magicBytes = await parser.getMagicBytes(entry, 16);
 *   if (isMZExecutable(magicBytes)) throw new Error('Executable detected');
 *
 *   // Option 2: Validate CRC only (memory efficient)
 *   const { valid, reason } = await parser.streamAndValidate(entry, true);
 *
 *   // Option 3: Get full data for validator that needs it
 *   const data = await parser.getArrayBuffer(entry);
 *   await validatePdf(new File([data], entry.name));
 *
 *   // Option 4: Process chunks (ultimate efficiency)
 *   await parser.streamWithCallback(entry, (chunk) => {
 *     // Process chunk (e.g., incremental hash, write to disk, etc.)
 *   });
 * }
 */
